import React, { useEffect, useRef, useState } from "react";
import styles from "./portfolioDelta.module.scss";
import classNames from "classnames";

const PortfolioDelta = ({ dataItem, dataItem2, title, tableHeight }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const observer = new window.ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    observer.observe(chartContainerRef.current);

    // Initial set
    setContainerWidth(chartContainerRef.current.offsetWidth);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && !window.matchMedia("print").matches) {
      if (containerRef.current) {
        containerRef.current.style.height = `${tableHeight - 26}px`;
      }
    }
  }, [tableHeight]);

  if (!dataItem || !dataItem.data || !dataItem2 || !dataItem2.data) {
    return null;
  }

  let maxValue = 0;
  const deltaData = dataItem.data.map((row) => {
    const matchingRow = dataItem2.data.find(
      (item) => item.label === row.label,
    ) || { weight: 0 };

    const configData = dataItem.configData;
    const displayLabel =
      configData && configData.rows
        ? configData.rows[row.label] || row.label
        : row.label;

    const delta = matchingRow.weight - row.weight;

    if (Math.abs(delta) > maxValue) {
      maxValue = Math.abs(delta);
    }

    return {
      label: displayLabel,
      delta: delta,
      portfolio1Weight: row.weight,
      portfolio2Weight: matchingRow.weight,
    };
  });

  maxValue = Math.ceil(maxValue / 5) * 5 + 5;

  let step = 5;
  if (containerWidth < 350) step = 20;
  else if (containerWidth < 550) step = 10;
  else step = 5;

  const labels = [];
  for (let i = -maxValue; i <= maxValue; i += step) {
    labels.push(`${i}%`);
  }

  return (
    <div className={styles.portfolioDeltaContainer} ref={containerRef}>
      {title && <h3 className={styles.title}>{title}</h3>}

      <div
        className={classNames(styles.chartContainer, styles.deltas)}
        ref={chartContainerRef}
      >
        {deltaData.map((row, index, array) => (
          <div
            key={index}
            className={`${styles.chartRow} ${index === array.length - 1 ? styles.lastRow : ""}`}
          >
            <div className={styles.chartColumn}>
              {/* Bar rendering */}
              {row.delta < 0 ? (
                <div
                  className={`${styles.bar} ${styles.barLeft} ${styles.negativeBar}`}
                  style={{
                    width: `${(Math.abs(row.delta) / maxValue) * 50}%`,
                    left: `${50 - (Math.abs(row.delta) / maxValue) * 50}%`,
                  }}
                >
                  <span className={`${styles.barLabel} ${styles.leftLabel}`}>
                    {row.delta.toFixed(2).replace(".", ",")}%
                  </span>
                </div>
              ) : row.delta > 0 ? (
                <div
                  className={`${styles.bar}  ${styles.barRight} ${styles.positiveBar}`}
                  style={{
                    width: `${(Math.abs(row.delta) / maxValue) * 50}%`,
                    left: "50%",
                  }}
                >
                  <span className={`${styles.barLabel} ${styles.rightLabel}`}>
                    {row.delta.toFixed(2).replace(".", ",")}%
                  </span>
                </div>
              ) : (
                <div
                  className={`${styles.bar} ${styles.zeroBar}`}
                  style={{
                    width: "0px",
                    left: "50%",
                  }}
                >
                  <span
                    className={`${styles.barLabel} ${styles.zeroLabel} ${styles.zeroLabelLeft}`}
                  >
                    0,0%
                  </span>
                  <span
                    className={`${styles.barLabel} ${styles.zeroLabel} ${styles.zeroLabelRight}`}
                  >
                    0,0%
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        <div className={styles.gridLines}>
          <div className={styles.centerLine}></div>
          {labels.map((label, i) => {
            if (i === labels.length - 1) return null;
            return (
              <div
                key={`grid-${i}`}
                className={styles.gridLine}
                style={{ left: `${(i / (labels.length - 1)) * 100}%` }}
              ></div>
            );
          })}
        </div>
      </div>

      {/* X-Axis Labels */}
      <div className={styles.xAxisContainer}>
        <div className={styles.xAxisLabels}>
          {labels.map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioDelta;
